import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { WA_PHYSICS } from '../core/constants';
import { getGameRNG } from '../core/rng';
import { computeSafeSpawnPosition } from './spawnHelper';

export const WeaponClusterBomb: WeaponDefinition = {
  id: 'cluster',
  name: 'Cluster Bomb',
  icon: 'cluster.png',
  category: 'thrown',
  maxAmmo: -1,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: true,
  timerRange: [1, 5],
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, timer, world } = ctx;

    const maxSpeed = 750;
    const speed = (power / 100) * maxSpeed;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const spawnDistance = 20;
    const spawn = computeSafeSpawnPosition(worm, aimRad, spawnDistance, world);

    const cluster = new Projectile({
      x: spawn.x,
      y: spawn.y,
      vx: Math.cos(aimRad) * speed,
      vy: -Math.sin(aimRad) * speed,
      type: 'cluster',
      timer: timer,
      bounceH: WA_PHYSICS.CLUSTER_BOMB.BOUNCE_HORIZONTAL,
      bounceV: WA_PHYSICS.CLUSTER_BOMB.BOUNCE_VERTICAL,
      friction: WA_PHYSICS.CLUSTER_BOMB.FRICTION,
      windAffected: false,
      explosionRadius: WA_PHYSICS.CLUSTER_BOMB.EXPLOSION_RADIUS,
      maxDamage: WA_PHYSICS.CLUSTER_BOMB.MAX_DAMAGE,
      ownerId: worm.id,
    });

    cluster.onExplode = (x, y) => {
      spawnClusterlets(x, y, worm.id, world, world.getNetworkManager?.());
    };

    return {
      success: true,
      entities: [cluster],
    };
  },
};

function spawnClusterlets(
  x: number,
  y: number,
  ownerId: string,
  world: WeaponContext['world'],
  networkManager?: any,
): void {
  const count = WA_PHYSICS.CLUSTER_BOMB.CLUSTER_COUNT;
  const rng = getGameRNG();
  const clusters: any[] = [];

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count - Math.PI / 2;
    const speed = rng.nextFloat(150, 250);

    const clusterlet = new Projectile({
      x: x,
      y: y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 100,
      type: 'clusterlet',
      timer: rng.nextFloat(2, 3),
      bounceH: 0.8,
      bounceV: 0.4,
      windAffected: false,
      explosionRadius: WA_PHYSICS.CLUSTER_BOMB.CLUSTER_RADIUS,
      maxDamage: WA_PHYSICS.CLUSTER_BOMB.CLUSTER_DAMAGE,
      ownerId: ownerId,
    });

    world.addEntity(clusterlet);

    clusters.push({
      id: clusterlet.id,
      type: clusterlet.type,
      position: { x: clusterlet.position.x, y: clusterlet.position.y },
      velocity: { x: clusterlet.velocity.x, y: clusterlet.velocity.y },
      timer: clusterlet.timer,
      rotation: clusterlet.rotation,
      isOnGround: clusterlet.isOnGround,
      explosionRadius: clusterlet.explosionRadius,
      maxDamage: clusterlet.maxDamage,
      ownerId: clusterlet.ownerId,
    });
  }

  if (networkManager) {
    networkManager.broadcastGameEvent({
      type: 'clusterSpawn',
      projectiles: clusters,
    });
  }
}
